import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Loading from "../../Common/Loading";

const AVATAR_PAGE_SIZE = 10;
const STUDENT_PAGE_SIZE = 1000;
const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;
const API_URL = `${API_BASE_URL}api/free_avatar_usage/`;

interface AvatarUsage {
  avatar: string;
  usage: number;
}

interface StudentRow {
  schoolCode: string;
  familyCode: string;
  login: string;
}

interface AvatarStudentsResponse {
  avatar: string;
  imageUrl: string;
  students: StudentRow[];
}

async function getJson<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    method: "GET",
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error(`Request failed (${response.status}): ${url}`);
  }
  return (await response.json()) as T;
}

const fetchAvatarUsage = () => getJson<{ avatars: AvatarUsage[] }>(API_URL);

const fetchAvatarStudents = (avatarName: string) =>
  getJson<AvatarStudentsResponse>(
    `${API_URL}students/?${new URLSearchParams({ avatar: avatarName }).toString()}`
  );


const CSS = `
.fau-scope, .fau-scope * { box-sizing: border-box; }

.fau-scope {
  font-family: Arial, sans-serif;
  font-size: 13px;
  color: #000;
}

.fau-root {
  width: 100%;
  min-height: 100%;
  margin: 0;
  padding: 0;
  background: #fff;
}

.fau-scope .fau-page { width: 100%; min-height: 100vh; padding: 0; margin: 0; }

.fau-scope .fau-page-title {
  width: 100%;
  height: 34px;
  padding: 7px 10px;
  background: #750d2b;
  color: #fff;
  text-align: center;
  font-size: 16px;
  font-weight: bold;
}

.fau-scope .fau-avatar-panel {
  display: block;
  width: 100%;
  min-height: 165px;
  margin: 0;
  padding: 0;
  background: #f0f7fc;
  text-align: left;
  overflow-x: auto;
}

.fau-scope #divAvatarGrid {
  display: block;
  width: 100%;
  min-height: 165px;
  margin: 0;
  padding: 0;
  background: #f0f7fc;
  text-align: left;
}

.fau-scope #divAvatarGrid table,
.fau-scope .fau-avatar-panel table.fau-avatar-table {
  display: table !important;
  width: auto !important;
  min-width: 0 !important;
  max-width: none !important;
  margin: 0 !important;
  padding: 0;
  border-collapse: collapse;
  border-spacing: 0;
  table-layout: auto !important;
  background: #fff;
  font-family: Arial, sans-serif;
  font-size: 15px;
}

.fau-scope #divAvatarGrid table th,
.fau-scope #divAvatarGrid table td,
.fau-scope .fau-avatar-panel table.fau-avatar-table th,
.fau-scope .fau-avatar-panel table.fau-avatar-table td {
  height: 28px;
  padding: 5px 9px;
  border: 1px solid #9bbcff;
  text-align: left;
  white-space: nowrap;
  font-weight: normal;
}

.fau-scope #divAvatarGrid table th,
.fau-scope .fau-avatar-panel table.fau-avatar-table th {
  background: #c8dcff;
  color: #164ca3;
}

.fau-scope #divAvatarGrid table th:first-child,
.fau-scope #divAvatarGrid table td:first-child,
.fau-scope .fau-avatar-panel table.fau-avatar-table th:first-child,
.fau-scope .fau-avatar-panel table.fau-avatar-table td:first-child {
  width: 38px !important;
  min-width: 38px;
}

.fau-scope #divAvatarGrid table tbody tr:nth-child(odd),
.fau-scope .fau-avatar-panel table.fau-avatar-table tbody tr:nth-child(odd) { background: #f0f7fc; }

.fau-scope #divAvatarGrid table tbody tr:nth-child(even),
.fau-scope .fau-avatar-panel table.fau-avatar-table tbody tr:nth-child(even) { background: #fff; }

.fau-scope #divAvatarGrid table tbody tr:hover,
.fau-scope .fau-avatar-panel table.fau-avatar-table tbody tr:hover { background: #ffa500; color: #000; }

.fau-scope .fau-usage-link {
  display: inline;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: #000;
  font-family: Arial, sans-serif;
  font-size: 13px;
  text-decoration: underline;
  cursor: pointer;
}
.fau-scope .fau-usage-link:hover { text-decoration: underline; color: #164ca3; }
.fau-scope .fau-usage-zero { color: #777; }

.fau-scope .fau-empty-message {
  width: 100%;
  min-height: 80px;
  padding: 20px;
  background: #f0f7fc;
  color: #666;
  text-align: center;
}

.fau-scope #avatarPagination,
.fau-scope .fau-pagination {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  width: 100%;
  min-height: 0;
  margin: 0;
  padding: 8px 0;
  background: #f0f7fc;
}

.fau-scope .fau-page-btn {
  min-width: 30px;
  padding: 5px 9px;
  border: 1px solid #9bbcff;
  background: #fff;
  color: #164ca3;
  cursor: pointer;
}
.fau-scope .fau-page-btn.fau-active { background: #c8dcff; font-weight: bold; }
.fau-scope .fau-page-btn:disabled { opacity: 0.4; cursor: default; }

.fau-scope .fau-footer { width: 100%; margin: 0; padding: 10px 2px; background: #fff; text-align: left; }

.fau-scope .fau-close-page-btn {
  display: inline-block;
  width: 120px;
  height: 32px;
  padding: 5px 10px;
  border: 1px solid #aaa;
  background: #eee;
  color: #000;
  font-family: Arial, sans-serif;
  font-size: 13px;
  cursor: pointer;
}
.fau-scope .fau-close-page-btn:hover { background: #e3e3e3; }

.fau-scope .fau-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: none;
  align-items: center;
  justify-content: center;
  padding: 15px;
  background: rgba(0, 0, 0, 0.45);
}
.fau-scope .fau-modal-overlay.fau-open { display: flex; }

.fau-scope .fau-modal-box {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 950px;
  max-height: 90vh;
  overflow: hidden;
  border: 1px solid #9bbcff;
  background: #fff;
}

.fau-scope .fau-modal-header {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid #9bbcff;
  background: #c8dcff;
  color: #164ca3;
}
.fau-scope .fau-modal-title { font-size: 17px; font-weight: bold; }
.fau-scope .fau-modal-x {
  padding: 0;
  border: 0;
  background: transparent;
  color: #164ca3;
  font-size: 25px;
  cursor: pointer;
}

.fau-scope .fau-modal-summary {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 14px;
  padding: 12px 15px;
  border-bottom: 1px solid #9bbcff;
  background: #fff;
}

.fau-scope .fau-avatar-placeholder {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 65px;
  height: 65px;
  border: 1px solid #9bbcff;
  background: #f0f7fc;
}
.fau-scope .fau-avatar-placeholder img { width: 100%; height: 100%; object-fit: contain; }

.fau-scope .fau-summary-text { line-height: 1.8; }
.fau-scope .fau-summary-count { color: #164ca3; font-weight: bold; }

.fau-scope .fau-modal-content {
  flex: 1;
  min-height: 0;
  padding: 12px;
  overflow: auto;
  background: #f0f7fc;
}
.fau-scope .fau-modal-content .fau-table-scroll {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: auto;
  max-height: 400px;
}

.fau-scope .fau-modal-content table.fau-student-table {
  width: 100% !important;
  max-width: 100%;
  margin: 0;
  border-collapse: collapse;
  table-layout: auto;
  background: #fff;
  font-family: Arial, sans-serif;
  font-size: 13px;
}

.fau-scope .fau-student-table th,
.fau-scope .fau-student-table td {
  padding: 8px 10px;
  border: 1px solid #9bbcff;
  text-align: left;
  white-space: nowrap;
}
.fau-scope .fau-student-table th { background: #c8dcff; color: #164ca3; font-weight: normal; }
.fau-scope .fau-student-table tbody tr:nth-child(odd) { background: #f0f7fc; }
.fau-scope .fau-student-table tbody tr:nth-child(even) { background: #fff; }
.fau-scope .fau-student-table tbody tr:hover { background: #ffa500; }

.fau-scope .fau-student-count { margin-top: 8px; background: #f0f7fc; color: #164ca3; font-size: 12px; }

.fau-scope .fau-modal-footer {
  display: flex;
  flex-shrink: 0;
  justify-content: flex-end;
  padding: 10px 12px;
  border-top: 1px solid #9bbcff;
  background: #f0f7fc;
}
.fau-scope .fau-modal-close-btn {
  min-width: 100px;
  padding: 7px 15px;
  border: 1px solid #aaa;
  background: #eee;
  cursor: pointer;
}

@media (max-width: 650px) {
  .fau-scope .fau-modal-overlay { padding: 5px; }
  .fau-scope .fau-modal-box { max-height: 95vh; }
  .fau-scope .fau-modal-content { overflow: auto; }
  .fau-scope .fau-student-table th,
  .fau-scope .fau-student-table td { padding: 7px; }
}
`;

// ---------------------------------------------------------------------------
// Pagination (fn_RenderPagination)
// ---------------------------------------------------------------------------
interface PaginationProps {
  containerId: string;
  totalItems: number;
  pageSize: number;
  currentPage: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  containerId,
  totalItems,
  pageSize,
  currentPage,
  onPageChange,
}) => {
  const totalPages = Math.ceil(totalItems / pageSize);

  if (totalPages <= 1) {
    return <div id={containerId} className="fau-pagination" />;
  }

  const pages: number[] = [];
  for (let p = 1; p <= totalPages; p++) pages.push(p);

  return (
    <div id={containerId} className="fau-pagination">
      <button
        type="button"
        className="fau-page-btn"
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
      >
        ‹
      </button>
      {pages.map((p) => (
        <button
          key={p}
          type="button"
          className={"fau-page-btn" + (p === currentPage ? " fau-active" : "")}
          onClick={() => onPageChange(p)}
        >
          {p}
        </button>
      ))}
      <button
        type="button"
        className="fau-page-btn"
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
      >
        ›
      </button>
    </div>
  );
};


type StudentLoadState = "idle" | "loading" | "loaded" | "error";

interface FreeAvatarUsageProps {
  onClose?: () => void;
}

const FreeAvatarUsage: React.FC<FreeAvatarUsageProps> = ({ onClose }) => {
  // Avatar grid
  const [avatars, setAvatars] = useState<AvatarUsage[]>([]);
  const [avatarsLoaded, setAvatarsLoaded] = useState<boolean>(false);
  const [avatarPage, setAvatarPage] = useState<number>(1);

  // Student modal
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedAvatarName, setSelectedAvatarName] = useState<string>("");
  const [modalCount, setModalCount] = useState<number>(0);
  const [selectedAvatarImageUrl, setSelectedAvatarImageUrl] = useState<string>("");
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [studentLoadState, setStudentLoadState] = useState<StudentLoadState>("idle");
  const [loadedAvatar, setLoadedAvatar] = useState<string>(""); // data-avatar
  const [studentPage, setStudentPage] = useState<number>(1);

  const requestIdRef = useRef<number>(0);

  // <title>Free Avatar Usages</title>
  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Free Avatar Usages";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  // Page_Load -> LoadAvatarUsage()
  const loadAvatars = useCallback(async (): Promise<void> => {
    try {
      const data = await fetchAvatarUsage();
      setAvatars(data.avatars);
    } catch (err) {
      console.error("Free avatar usage: load failed", err);
      setAvatars([]);
    } finally {
      setAvatarsLoaded(true);
    }
  }, []);

  useEffect(() => {
    void loadAvatars();
    setAvatarPage(1);
  }, [loadAvatars]);

  // Escape closes the modal
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setModalOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  // fn_Close()
  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      // Go back to the previous page in the browser history
      window.history.back();
    }
  };

  // fn_CloseStudentModal()
  const handleCloseStudentModal = () => setModalOpen(false);

  // fn_OverlayClick(event)
  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) handleCloseStudentModal();
  };

  // fn_ViewStudents(avatarName, usageCount)
  const handleViewStudents = (avatarName: string, usageCount: number) => {
    if (!usageCount || usageCount <= 0) return;

    setSelectedAvatarName(avatarName);
    setStudentPage(1);
    setModalCount(usageCount);
    setModalOpen(true);

    // Reuse already-loaded details if the same avatar is selected.
    if (loadedAvatar === avatarName) {
      setStudentPage(1);
      return;
    }

    setStudentLoadState("loading");
    setStudents([]);

    const requestId = ++requestIdRef.current;

    // btnLoadStudents_Click -> LoadStudentDetails()
    void fetchAvatarStudents(avatarName)
      .then((data) => {
        if (requestId !== requestIdRef.current) return;
        const list = data.students;
        setStudents(list);
        setStudentLoadState("loaded");
        setSelectedAvatarImageUrl(data.imageUrl);
        if (list.length > 0) {
          setModalCount(list.length);
          setLoadedAvatar(avatarName);
        } else {
          setLoadedAvatar("");
        }
        setStudentPage(1);
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) return;
        console.error("Free avatar usage: load students failed", err);
        setStudents([]);
        setStudentLoadState("error");
        setLoadedAvatar("");
      });

    // The old postback also re-ran LoadAvatarUsage() and reset the grid to page 1.
    void loadAvatars();
    setAvatarPage(1);
  };

  // fn_RenderAvatarPage(page)
  const handleAvatarPageChange = (page: number) => {
    const totalPages = Math.max(1, Math.ceil(avatars.length / AVATAR_PAGE_SIZE));
    setAvatarPage(Math.min(Math.max(1, page), totalPages));
  };

  // fn_RenderStudentPage(page)
  const handleStudentPageChange = (page: number) => {
    const totalPages = Math.max(1, Math.ceil(students.length / STUDENT_PAGE_SIZE));
    setStudentPage(Math.min(Math.max(1, page), totalPages));
  };

  // Derived values
  const avatarTotalPages = Math.max(1, Math.ceil(avatars.length / AVATAR_PAGE_SIZE));
  const currentAvatarPage = Math.min(Math.max(1, avatarPage), avatarTotalPages);
  const avatarStart = (currentAvatarPage - 1) * AVATAR_PAGE_SIZE;
  const visibleAvatars = avatars.slice(avatarStart, avatarStart + AVATAR_PAGE_SIZE);

  const studentsReady = studentLoadState === "loaded" && students.length > 0;
  const studentTotalPages = Math.max(1, Math.ceil(students.length / STUDENT_PAGE_SIZE));
  const currentStudentPage = Math.min(Math.max(1, studentPage), studentTotalPages);
  const studentStart = (currentStudentPage - 1) * STUDENT_PAGE_SIZE;
  const visibleStudents = students.slice(studentStart, studentStart + STUDENT_PAGE_SIZE);

  const studentPageInfo = studentsReady
    ? "Showing " +
      (studentStart + 1) +
      "–" +
      Math.min(currentStudentPage * STUDENT_PAGE_SIZE, students.length) +
      " of " +
      students.length +
      " students"
    : "";

  // BuildAvatarTable
  const renderAvatarGrid = () => {
    if (!avatarsLoaded) return <Loading />;

    if (avatars.length === 0) {
      return <div className="fau-empty-message">No free avatars found.</div>;
    }

    return (
      <table className="fau-avatar-table">
        <thead>
          <tr>
            <th style={{ width: "55px" }}>#</th>
            <th>Avatar</th>
            <th style={{ width: "100%" }}>Usage</th>
          </tr>
        </thead>
        <tbody>
          {visibleAvatars.map((item, index) => (
            <tr key={item.avatar}>
              <td>{avatarStart + index + 1}</td>
              <td className="fau-avatar-name">{item.avatar}</td>
              <td>
                {item.usage > 0 ? (
                  <button
                    type="button"
                    className="fau-usage-link"
                    onClick={() => handleViewStudents(item.avatar, item.usage)}
                  >
                    {item.usage}
                  </button>
                ) : (
                  <span className="fau-usage-zero">0</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  // BuildStudentTable
  const renderStudentGrid = () => {
    if (studentLoadState === "loading") {
      return <Loading />;
    }
    if (studentLoadState !== "loaded") return null;

    if (students.length === 0) {
      return <div className="fau-empty-message">No students are using this avatar.</div>;
    }

    return (
      <div className="fau-table-scroll">
        <table className="fau-student-table">
          <thead>
            <tr>
              <th>#</th>
              <th>School Code</th>
              <th>Family Code</th>
              <th>Login</th>
            </tr>
          </thead>
          <tbody>
            {visibleStudents.map((student, index) => (
              <tr key={`${studentStart + index}-${student.login}`}>
                <td>{studentStart + index + 1}</td>
                <td>{student.schoolCode}</td>
                <td>{student.familyCode}</td>
                <td>{student.login}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
    <div className="fau-root fau-scope">
      <style>{CSS}</style>

      <div className="fau-page">
        <div className="fau-page-title">Free Avatar Usages</div>

        <div className="fau-avatar-panel">
          <div id="divAvatarGrid">{renderAvatarGrid()} </div>
          <Pagination
            containerId="avatarPagination"
            totalItems={avatars.length}
            pageSize={AVATAR_PAGE_SIZE}
            currentPage={currentAvatarPage}
            onPageChange={handleAvatarPageChange}
          />
        </div>

        <div className="fau-footer">
          <button type="button" className="fau-close-page-btn" onClick={handleClose}>
            Close
          </button>
        </div>
      </div>

    </div>

      {/* Student details modal (portal to <body> so no parent CSS/transform can break position:fixed) */}
      {createPortal(
        <div className="fau-scope">
      <div
        id="divStudentModal"
        className={"fau-modal-overlay" + (modalOpen ? " fau-open" : "")}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modalTitle"
        onClick={handleOverlayClick}
      >
        <div className="fau-modal-box">
          <div className="fau-modal-header">
            <div id="modalTitle" className="fau-modal-title">
              {" "}
              Students Using Avatar{" "}
            </div>
            <button
              type="button"
              className="fau-modal-x"
              aria-label="Close"
              onClick={handleCloseStudentModal}
            >
              ×
            </button>
          </div>

          <div className="fau-modal-summary">
            <div className="fau-avatar-placeholder">
              <img
                src={selectedAvatarImageUrl || undefined}
                alt="Selected Avatar"
                width={65}
                height={65}
                style={{ width: "65px", height: "65px", objectFit: "contain", borderRadius: "8px" }}
              />
            </div>
            <div className="fau-summary-text">
              <div>
                {" "}
                Avatar Name: <strong id="lblModalAvatar">{selectedAvatarName}</strong>
              </div>
              <div>
                {" "}
                Total Students:{" "}
                <span id="lblModalCount" className="fau-summary-count">
                  {modalCount}
                </span>
              </div>
            </div>
          </div>

          <div className="fau-modal-content">
            <div id="divStudentGrid" data-avatar={loadedAvatar || undefined}>
              {renderStudentGrid()}{" "}
            </div>
            <Pagination
              containerId="studentPagination"
              totalItems={studentsReady ? students.length : 0}
              pageSize={STUDENT_PAGE_SIZE}
              currentPage={currentStudentPage}
              onPageChange={handleStudentPageChange}
            />
            <div id="studentPageInfo" className="fau-student-count">
              {studentPageInfo}
            </div>
          </div>

          <div className="fau-modal-footer">
            <button type="button" className="fau-modal-close-btn" onClick={handleCloseStudentModal}>
              Close
            </button>
          </div>
        </div>
      </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default FreeAvatarUsage;